import { useState } from 'react';
import { toast } from 'react-toastify';
import { CreditCard, Download } from 'lucide-react';
import { createPaymentRedirect } from '../../../../../helper/createPaymentRedirect';
import { handlePaymentRedirect } from '../../../../../helper/handlePaymentRedirect';
import { setPendingPayment } from '../../../../../helper/paymentPendingStorage';
import UserService from '../../../../../services/User.service';
import type { ProcessedPayment } from '../PaymentsTable';

interface ActionButtonProps {
  payment: ProcessedPayment;
}

export const ActionButton: React.FC<ActionButtonProps> = ({ payment }) => {
  const [downloadingId, setDownloadingId] = useState<number | string | null>(null);
  const isDownloading = downloadingId === payment.id;

  const [isLoading, setLoading] = useState(false);

  const payPayment = async (): Promise<void> => {
    setLoading(true);
    try {
      const redirectUrl = createPaymentRedirect();
      const response = await UserService.payPayment(payment.id, redirectUrl);

      if (!response.error && response.data && response.data.redirectUrl) {
        const paymentUrl = response.data.redirectUrl;
        setPendingPayment({
          startedAt: Date.now(),
          sourcePath: `${window.location.pathname}${window.location.search}`,
          novalnetUrl: paymentUrl,
          contextType: 'payment',
          contextId: payment.id,
        });
        handlePaymentRedirect(paymentUrl);

        return;
      } else {
        console.error('API Error:', response.message);
        toast.error('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } catch (error) {
      console.error('API Error:', error);
      toast.error('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (): Promise<void> => {
    const invoiceIdentifier = payment.invoiceNumber;
    const paymentId = payment.id;
    const isReservation = payment.productCode.includes('reservation');

    setDownloadingId(paymentId);
    try {
      const response = await UserService.getInvoice(paymentId);

      if (response.error || !response.data) {
        console.error('API Error fetching invoice:', response.message);
        toast.error(`Fehler beim Herunterladen der Rechnung: ${response.message || 'Keine Rechnungsdaten erhalten.'}`);
        setDownloadingId(null);

        return;
      }

      if (!(response.data instanceof Blob)) {
        console.error('API Error: Response data is not a Blob', response.data);
        toast.error('Fehler beim Herunterladen: Ungültiges Datenformat erhalten.');
        setDownloadingId(null);

        return;
      }

      const blob = response.data;
      const filename = isReservation
        ? 'zahlungsanforderung'
        : `rechnung-${String(invoiceIdentifier).replace(/[^a-z0-9_-]/gi, '_')}.pdf`;

      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.warn('Download attempt outside of browser context.');
        toast.warn('Download ist nur im Browser verfügbar.');
      }
    } catch (unexpectedError) {
      console.error('Unexpected download error:', unexpectedError);
      toast.error('Ein unerwarteter Fehler ist beim Download aufgetreten.');
    } finally {
      setDownloadingId(null);
    }
  };

  switch (payment.displayStatus) {
    case 'Paid':
      if (!payment.invoiceNumber) {
        return null;
      }

      return (
        <button
          onClick={() => handleDownloadInvoice()}
          disabled={isDownloading}
          className='inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'
          title='Rechnung herunterladen'
        >
          <Download className='h-4 w-4 mr-1' />
          {isDownloading ? 'Lädt...' : 'Rechnung'}
        </button>
      );
    case 'Started':
    case 'Unpaid':
      return (
        <button
          onClick={() => payPayment()}
          rel='noopener noreferrer'
          className='inline-flex no-underline items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transform hover:scale-105 transition-transform duration-150 ease-in-out'
          title='Zahlung jetzt durchführen'
        >
          <CreditCard className='h-5 w-5 mr-2' />
          {isLoading ? '...' : payment.displayStatus === 'Started' ? 'Zahlung fortsetzen' : 'Bezahlen'}
        </button>
      );
    case 'Refunded':
    case 'Expired':
    case 'Unknown':
    default:
      return null;
  }
};
